import { motion } from "framer-motion";
import { CheckCircle2, FileText, Printer, Zap, Shield, Target } from "lucide-react";
import { useState } from "react";
import { ContactModal } from "@/components/ContactModal";

const FlyerDesignServices = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <main className="min-h-screen bg-gradient-to-b from-background to-accent-navy/30">
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            Professional Flyer Design Services
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-muted-foreground mb-8"
          >
            Get eye-catching flyers that drive action. Our strategic flyer design services deliver marketing materials that convert. From concept to print, we ensure campaign success.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Start Your Campaign
          </motion.button>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Our Flyer Design Process
          </motion.h2>
          <div className="space-y-6">
            {[
              { step: 1, title: "Campaign Strategy", desc: "We analyze your marketing objectives, target audience, and campaign goals to inform design decisions." },
              { step: 2, title: "Concept Development", desc: "Multiple flyer concepts created based on campaign strategy and brand guidelines." },
              { step: 3, title: "Design & Refinement", desc: "Collaborative feedback sessions to optimize for conversion and brand consistency." },
              { step: 4, title: "Print Production", desc: "Final files prepared with print specifications and production coordination." },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-4 items-start"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-coral/20 flex items-center justify-center text-coral font-bold">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services We Offer */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Flyer Design Services We Offer
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Target, title: "Event Flyers", desc: "Promotional materials for events, conferences, and special occasions." },
              { icon: Zap, title: "Promotional Flyers", desc: "Marketing materials for product launches and special offers." },
              { icon: FileText, title: "Informational Flyers", desc: "Educational content for corporate communications and brand awareness." },
              { icon: Printer, title: "Digital Adaptations", desc: "Flyer designs optimized for digital distribution and social media." },
            ].map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <service.icon className="w-8 h-8 text-coral mb-4" />
                <h3 className="font-semibold text-lg mb-2">{service.title}</h3>
                <p className="text-muted-foreground">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Types of Flyers */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Types of Marketing Flyers We Design
          </motion.h2>
          <div className="space-y-4">
            {[
              { type: "Event Promotion", desc: "Flyers designed to drive attendance and engagement for events." },
              { type: "Product Launches", desc: "Eye-catching designs that highlight product features and benefits." },
              { type: "Special Offers", desc: "Promotional materials that communicate discounts and limited-time deals." },
              { type: "Corporate Communications", desc: "Professional flyers for internal announcements and company updates." },
              { type: "Brand Awareness", desc: "Strategic designs that build brand recognition and recall." },
            ].map((flyer, index) => (
              <motion.div
                key={flyer.type}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-4 rounded-lg border border-gray-800"
              >
                <h3 className="font-semibold text-lg mb-1">{flyer.type}</h3>
                <p className="text-muted-foreground">{flyer.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Print Production */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Print Production Expertise
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Printer, title: "Paper Selection", desc: "Expert guidance on paper types, weights, and finishes for optimal results." },
              { icon: FileText, title: "Size Specifications", desc: "Standard and custom sizes with proper bleed and margin requirements." },
              { icon: Shield, title: "Color Modes", desc: "CMYK conversion and color accuracy for print production." },
              { icon: Zap, title: "File Formats", desc: "Print-ready files in all required formats (PDF, EPS, AI)." },
            ].map((expertise, index) => (
              <motion.div
                key={expertise.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <expertise.icon className="w-8 h-8 text-coral mb-4" />
                <h3 className="font-semibold text-lg mb-2">{expertise.title}</h3>
                <p className="text-muted-foreground">{expertise.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Flyer Design Pricing & Packages
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: "Single Flyer", price: "$149", features: ["1 flyer design", "2 revisions", "Print-ready files", "2-day delivery"] },
              { name: "Campaign Pack", price: "$399", features: ["4 flyer designs", "Unlimited revisions", "Brand consistency", "5-day delivery"] },
              { name: "Design + Print", price: "$599", features: ["5 flyer designs", "Print coordination", "Premium paper", "Priority support"] },
            ].map((pkg, index) => (
              <motion.div
                key={pkg.name}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <h3 className="font-semibold text-xl mb-2">{pkg.name}</h3>
                <div className="text-3xl font-bold text-coral mb-4">{pkg.price}</div>
                <ul className="space-y-2">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-coral" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Why Choose Koech Creatives for Flyer Design
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Target, title: "Marketing-Focused", desc: "We design with campaign objectives and conversion goals in mind." },
              { icon: Printer, title: "Print Expertise", desc: "Deep knowledge of print production and technical specifications." },
              { icon: Zap, title: "Fast Turnaround", desc: "Quick delivery without compromising on quality or attention to detail." },
              { icon: Shield, title: "Satisfaction Guarantee", desc: "We refine until you're completely satisfied with the results." },
            ].map((reason, index) => (
              <motion.div
                key={reason.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-4"
              >
                <reason.icon className="w-8 h-8 text-coral flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-lg mb-1">{reason.title}</h3>
                  <p className="text-muted-foreground">{reason.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-4"
          >
            Ready to Create Impactful Marketing Materials?
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-xl text-muted-foreground mb-8"
          >
            Let's design flyers that drive real results for your campaign.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Get a Free Consultation
          </motion.button>
        </div>
      </section>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </main>
  );
};

export default FlyerDesignServices;
